import { Components, CSSInterpolation, Theme } from '@mui/material'
import type { AvatarOwnProps } from '@mui/material/Avatar'

declare module '@mui/material/Avatar' {
  interface AvatarOwnProps {
    // Avatar doesn't have a color prop in MUI, so we add one
    color?: 'default' | 'brand-muted' | 'gold' | 'mint' | 'plum' | 'lime'
    // The avatar's dimensions; the font-size scales with it
    size?: 16 | 20 | 24 | 32 | 56
    ['data-is-surplus']?: boolean
  }

  interface AvatarPropsVariantOverrides {
    // We only use square and circular, not rounded.
    rounded: false
  }
}

declare module '@mui/material/AvatarGroup' {
  interface AvatarGroupPropsVariantOverrides {
    rounded: false
  }
}

// `letter-spacing` adds its gap *after* the last character too, so centred
// initials sit half a letter-space left of the avatar's centre. `text-indent`
// offsets a centred line by half its value, so indenting by the full
// letter-spacing cancels that trailing gap out. Images and the fallback icon are
// flex items, not inline content, so they're unaffected.
const centreLetterSpacedText = <T extends { letterSpacing?: string | number }>(style: T) => ({
  ...style,
  textIndent: style.letterSpacing,
})

export const MuiAvatar: Components<Omit<Theme, 'components'>>['MuiAvatar'] = {
  defaultProps: {
    variant: 'circular',
    color: 'default',
    size: 24,
    slotProps: {
      root: ({ color, size }) => ({
        'data-color': color,
        'data-size': size,
        // Don't leak our custom props to the DOM as invalid attributes
        color: undefined,
        size: undefined,
      }),
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderWidth: theme.strokeWidth.default,
      borderStyle: 'solid',
    }),
  },
  variants: [
    // Size — width, height and font-size scale together
    {
      props: { size: 16 },
      style: ({ theme }) => ({
        width: 16,
        height: 16,
        ...centreLetterSpacedText(theme.typography['extraSmall-semiBold']),
        fontSize: 6,
      }),
    },
    {
      props: { size: 20 },
      style: ({ theme }) => ({
        width: 20,
        height: 20,
        ...centreLetterSpacedText(theme.typography['extraSmall-semiBold']),
        fontSize: 8,
      }),
    },
    {
      props: { size: 24 },
      style: ({ theme }) => ({
        width: 24,
        height: 24,
        ...centreLetterSpacedText(theme.typography['extraSmall-semiBold']),
      }),
    },
    {
      props: { size: 32 },
      style: ({ theme }) => ({
        width: 32,
        height: 32,
        ...centreLetterSpacedText(theme.typography['small-bold']),
      }),
    },
    {
      props: { size: 56 },
      style: ({ theme }) => ({
        width: 56,
        height: 56,
        ...centreLetterSpacedText(theme.typography['display-h3']),
      }),
    },
    // Shape
    // Convention: circular for human users, square for non-human entities (suppliers, products, integrations, etc).
    {
      props: { variant: 'square' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-sm'],
      }),
    },
    // A fixed radius reads proportionally rounder the smaller the avatar gets, so the smallest size
    // takes the tighter one. Must stay after the shape, which it overrides.
    {
      props: (props) => props.variant === 'square' && props.size === 16,
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-xs'],
      }),
    },
    // Colour
    {
      props: { color: 'default' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.bg.brand.default,
        color: theme.palette.text.brand.onBrand,
      }),
    },
    {
      props: { color: 'brand-muted' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.bg.brand.muted,
        color: theme.palette.text.brand.onBrandMuted,
      }),
    },
    {
      props: { color: 'gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.gold.muted,
        borderColor: theme.palette.border.utility.gold.muted,
        color: theme.palette.text.utility.gold.default,
      }),
    },
    {
      props: { color: 'mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.mint.muted,
        borderColor: theme.palette.border.utility.mint.muted,
        color: theme.palette.text.utility.mint.default,
      }),
    },
    {
      props: { color: 'plum' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.plum.muted,
        borderColor: theme.palette.border.utility.plum.muted,
        color: theme.palette.text.utility.plum.default,
      }),
    },
    {
      props: { color: 'lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.lime.muted,
        borderColor: theme.palette.border.utility.lime.muted,
        color: theme.palette.text.utility.lime['on-lime-muted'],
      }),
    },
  ],
}
const avatarSizes = [16, 20, 24, 32, 56] as const satisfies readonly NonNullable<AvatarOwnProps['size']>[]

const avatarGroupOverlapBySize = {
  16: 4,
  20: 4,
  24: 6,
  32: 8,
  56: 8,
} as const

const avatarGroupChild = ':is(.MuiAvatarGroup-avatar, .MuiAvatar-root):not([data-is-surplus="true"])'

// Subtract the next avatar's silhouette from an opaque mask. Without a viewBox, the SVG's
// viewport follows mask-size while square corner radii keep their theme-defined dimensions.
const avatarGroupCutoutMask = (radius: string) => {
  const silhouette = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><rect width="100%" height="100%" rx="${radius}"/></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(silhouette)}"), linear-gradient(#000 0 0)`
}

export const MuiAvatarGroup: Components<Omit<Theme, 'components'>>['MuiAvatarGroup'] = {
  defaultProps: {
    slotProps: {
      surplus: {
        'data-is-surplus': true,
      },
    },
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      '--AvatarGroup-overlap': '-6px',
      '--AvatarGroup-ringWidth': theme.strokeWidth.focus,
      '--AvatarGroup-offset': ownerState.spacing === 'medium' ? 'var(--AvatarGroup-overlap)' : 'var(--AvatarGroup-spacing)',
      alignItems: 'center',
      // Derive the group's overlap, rings and overflow typography from the size of the avatars inside it.
      ...avatarSizes.reduce<Record<string, CSSInterpolation>>((acc, size) => {
        acc[`&:has(.MuiAvatar-root[data-size="${size}"]:not([data-is-surplus="true"]))`] = {
          '--AvatarGroup-overlap': `-${avatarGroupOverlapBySize[size]}px`,
          '--AvatarGroup-ringWidth': size <= 20 ? theme.strokeWidth.strong : theme.strokeWidth.focus,
          '& [data-is-surplus="true"]': {
            ...centreLetterSpacedText(theme.typography[size >= 32 ? 'small-bold' : 'extraSmall-semiBold']),
            ...(size === 16 && { fontSize: 6 }),
            marginLeft: theme.spacing(size >= 32 ? 1 : 0.5),
          },
        }
        acc[
          `& > ${avatarGroupChild}:is([data-size="${size}"], :has(.MuiAvatar-root[data-size="${size}"])) + ${avatarGroupChild}`
        ] = {
          '--AvatarGroup-cutoutDiameter': `calc(${size}px + 2 * var(--AvatarGroup-ringWidth))`,
        }
        return acc
      }, {}),
      // Apply spacing to the direct child, including tooltip wrappers, only once.
      '& .MuiAvatar-root': {
        border: 'var(--AvatarGroup-ringWidth) solid transparent',
        backgroundClip: 'padding-box',
        marginLeft: 0,
      },
      '& > .MuiAvatarGroup-avatar, & > .MuiAvatar-root': {
        position: 'relative',
        flexShrink: 0,
        marginLeft: 'var(--AvatarGroup-offset)',
        '&:last-child': { marginLeft: 0 },
      },
      // MUI reverses the children, so the preceding sibling is the avatar immediately to the right.
      // Cut away the area behind it to expose the actual surface through the separation ring.
      [`& > ${avatarGroupChild} + ${avatarGroupChild}`]: {
        '--AvatarGroup-cutoutDiameter': 'calc(24px + 2 * var(--AvatarGroup-ringWidth))',
        maskImage: avatarGroupCutoutMask('50%'),
        maskSize: 'var(--AvatarGroup-cutoutDiameter) var(--AvatarGroup-cutoutDiameter), 100% 100%',
        maskPosition: 'calc(100% + var(--AvatarGroup-cutoutDiameter) + var(--AvatarGroup-offset)) center, 0 0',
        maskRepeat: 'no-repeat',
        maskComposite: 'exclude',
      },
      [`& > ${avatarGroupChild}:is(.MuiAvatar-square, :has(.MuiAvatar-square)) + ${avatarGroupChild}`]: {
        maskImage: avatarGroupCutoutMask(theme.shape['radius-sm']),
      },
      [`& > ${avatarGroupChild}:is(.MuiAvatar-square[data-size="16"], :has(.MuiAvatar-square[data-size="16"])) + ${avatarGroupChild}`]:
        {
          maskImage: avatarGroupCutoutMask(theme.shape['radius-xs']),
        },
      // MUI reverses the children. Bring the rightmost avatars forward as in Figma.
      isolation: 'isolate',
      ...Object.fromEntries(
        Array.from({ length: Math.max(ownerState.max ?? 5, 2) }, (_, index) => [
          `& > :nth-child(${index + 1})`,
          { zIndex: Math.max(ownerState.max ?? 5, 2) - index },
        ])
      ),
    }),
    avatar: ({ theme }) => ({
      '&[data-is-surplus="true"]': {
        ...centreLetterSpacedText(theme.typography['extraSmall-semiBold']),
        width: 'auto',
        height: 'auto',
        minWidth: 0,
        border: 0,
        borderRadius: 0,
        overflow: 'visible',
        // The overflow is plain text, separated from the final avatar by half a space.
        marginLeft: theme.spacing(0.5),
        backgroundColor: theme.palette.bg.invisible,
        color: theme.palette.text.muted,
      },
    }),
  },
}
